import React, { useState, useCallback, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
  Card, 
  CardMedia, 
  CardContent, 
  Typography, 
  Box, 
  Collapse, 
  CardActions, 
  Button, 
  Divider,
  Link,
  styled,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditIcon from '@mui/icons-material/Edit';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { logger } from '../utils/logger'; // Adjust the import path as necessary
import { keyframes } from '@mui/system';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

// Styled components
const ColorStrip = styled(Box)(({ theme }) => ({
  height: '10px',
  width: '100%',
  backgroundColor: theme.palette.secondary.main,
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 1
}));

const StyledCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: 'auto',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  overflow: 'visible',
  boxShadow: theme.shadows[3],
  '&:hover': {
    transform: theme.breakpoints.up('sm') ? 'translateY(-5px)' : 'none',
    boxShadow: theme.shadows[8],
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    minHeight: '400px', // Shorter on mobile
  },
  [theme.breakpoints.up('sm')]: {
    width: '280px',
    minHeight: '480px',
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    height: '180px', // Slightly shorter on mobile
  },
  [theme.breakpoints.up('sm')]: {
    height: '200px',
  }
}));

const MainOffer = styled(Typography)(({ theme }) => ({
  minHeight: '3rem',  // Accommodates 2 lines of text
  maxHeight: '3rem',
  lineHeight: '1.5rem',
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  color: theme.palette.text.primary,
  fontWeight: 500
}));

const OpenNowBadge = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  color: 'white',
  padding: '4px 12px',
  borderRadius: '16px',
  display: 'inline-flex',
  alignItems: 'center',
  marginBottom: '8px',
  fontWeight: 600,
  fontSize: '0.875rem',
  boxShadow: theme.shadows[2],
  '&::before': {
    content: '""',
    width: '8px',
    height: '8px',
    backgroundColor: 'white',
    borderRadius: '50%',
    marginRight: '8px',
    animation: 'pulse 2s infinite'
  }
}));

const pulseKeyframes = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.5;
  }
  70% {
    transform: scale(1);
    opacity: 0.75;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.5;
  }
`;

const DealCard = ({ venue }) => {
  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const mapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [suggestionOpen, setSuggestionOpen] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const recaptchaRef = useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [showMap, setShowMap] = useState(false);

  // Match database schema
  const {
    headline,
    address,
    main_offer,
    offer_details,
    days,
    phone,
    link,
    image_url,
    place_id,
    lat,
    lon,
    category,
    opening_hours,
    is_active
  } = venue;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const getMapUrl = () => {
    if (place_id) {
      return `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=place_id:${place_id}&language=en`;
    }
    // Fallback to address if no place_id available
    return `https://www.google.com/maps/embed/v1/place?key=${mapsApiKey}&q=${encodeURIComponent(address)}&language=en`;
  };

  const getFullUrl = (url) => {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
  };

  // Check if the venue is currently open
  const isOpenNow = () => {
    // First check if venue exists and has opening_hours
    if (!venue || !opening_hours) {
      return false;
    }
    
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTime = hours * 100 + minutes; // Convert to format like 1430 for 2:30 PM
    
    // Map day number to day name
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[day];
    
    // Get the time slots for today
    const todayHours = opening_hours[dayName] || [];
    
    // Check if current time falls within any time slot
    return todayHours.some(timeSlot => {
      if (!timeSlot || typeof timeSlot.start !== 'number' || typeof timeSlot.end !== 'number') {
        return false;
      }
      return currentTime >= timeSlot.start && currentTime <= timeSlot.end;
    });
  };

  const openNow = isOpenNow();

  const handleSuggestionOpen = () => {
    setSuggestionOpen(true);
  };

  const handleSuggestionClose = () => {
    setSuggestionOpen(false);
    setSuggestion('');
    setSubmitError(null);
    setRecaptchaToken(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleRecaptchaChange = (token) => {
    logger.log('reCAPTCHA callback triggered');
    if (token) {
      logger.log('reCAPTCHA token received');
    } else {
      logger.log('No reCAPTCHA token received');
    }
    setRecaptchaToken(token);
  };

  const handleRecaptchaError = () => {
    console.error('reCAPTCHA error occurred');
    setSubmitError('Error loading verification. Please try again.');
  };

  const handleSuggestionSubmit = async () => {
    if (!suggestion.trim() || !recaptchaToken) {
      setSubmitError('Please complete the verification');
      return;
    }
    
    setSubmitting(true);
    setSubmitError(null);

    try {
      logger.log('Submitting suggestion for venue:', venue.id);
      
      const suggestionData = {
        venue_id: venue.id || '',
        venue_name: venue.headline || '',
        suggestion: suggestion.trim(),
        created_at: serverTimestamp(),
        status: 'pending',
        // Store these in a separate field to avoid rules validation
        metadata: {
          admin_url: `/admin/venues/edit/${venue.id}`,
          recaptcha_token: recaptchaToken
        }
      };

      logger.log('Suggestion data:', suggestionData);
      
      const docRef = await addDoc(collection(db, 'venue_suggestions'), suggestionData);
      logger.log('Suggestion submitted successfully, ID:', docRef.id);

      handleSuggestionClose();
      // Show success message
      
    } catch (error) {
      logger.error('Error submitting suggestion:', error);
      setSubmitError('Failed to submit suggestion. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StyledCard data-venue-id={venue.id}>
      {/* <ColorStrip />*/}
      
      {/* Image container with overlay text */}
      <ImageContainer>
        <LazyLoadImage
          src={(image_url && image_url !== 'none') ? image_url : '/images/default_venue_photo.jpg'}
          placeholderSrc="/images/default_venue_photo.jpg"
          effect="blur"
          height={200}
          width="100%"
          onError={() => setImageError(true)}
          alt={headline || 'Venue photo'}
          wrapperProps={{
            style: {
              width: '100%',
              height: '100%',
              display: 'block'
            }
          }}
        />
        
        {/* Venue name and address overlay */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            color: 'white',
            p: 2,
            zIndex: 2,
            backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))'
          }}
        >
          <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
            {headline}
          </Typography>
          <Typography variant="body2">
            {address}
          </Typography>
        </Box>
      </ImageContainer>
      
      {/* Main content */}
      <CardContent sx={{ 
        flexGrow: 1, 
        pt: 2,
        pb: '8px !important' // Override default padding
      }}>
        <MainOffer gutterBottom>
          {main_offer || 'Happy Hour Deal'}
        </MainOffer>
        
        <Box sx={{ my: 1 }}> {/* Reduced from my: 1.5 */}
          {openNow && (
            <OpenNowBadge sx={{ 
              animation: `${pulseKeyframes} 2s infinite`,
              mb: 1 // Reduced from mb: 8
            }}>
              Open Now
            </OpenNowBadge>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {days || 'Check venue for hours'}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}> {/* Added mt: 1 */}
          <LocationOnIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {venue.distance ? `${venue.distance.toFixed(1)} km away` : 'Distance not available'}
          </Typography>
        </Box>
      </CardContent>
      
      <Divider />
      
      {/* Expand/collapse controls */}
      <CardActions disableSpacing>
        <Button
          onClick={handleExpandClick}
          aria-expanded={expanded}
          aria-label="show more"
          endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          sx={{ 
            width: '100%', 
            py: 1, 
            color: 'text.secondary',
            justifyContent: 'center'
          }}
        >
          {expanded ? 'Close Details' : 'View Details'}
        </Button>
      </CardActions>
      
      {/* Expandable details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {/* Contact information */}
          <Box sx={{ mb: 2 }}>
            {phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Link href={`tel:${phone}`} color="primary" underline="hover">
                  {phone}
                </Link>
              </Box>
            )}
            
            {link && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LanguageIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Link 
                  href={getFullUrl(link)}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  color="primary"
                  underline="hover"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  Website <OpenInNewIcon sx={{ ml: 0.5, fontSize: 14 }} />
                </Link>
              </Box>
            )}
          </Box>
                   
          {/* Google Maps embed */}
          <Box sx={{ mt: 2, width: '100%', height: '200px', borderRadius: 1, overflow: 'hidden' }}>
            <iframe
              title={`Map location for ${headline}`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              src={getMapUrl()}
            />
          </Box>
{/* Suggestion section */}
<Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
      <Button
        startIcon={<EditIcon />}
        onClick={handleSuggestionOpen}
        size="small"
        sx={{ width: '100%' }}
      >
        Suggest an Edit
      </Button>
    </Box>

        </CardContent>
      </Collapse>
      {/* Suggestion Dialog */}
<Dialog 
  open={suggestionOpen} 
  onClose={handleSuggestionClose}
  fullWidth
  maxWidth="sm"
>
  <DialogTitle>Suggest an Edit</DialogTitle>
  <DialogContent>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Help us keep the information accurate and up-to-date by suggesting changes.
    </Typography>
    <TextField
      autoFocus
      multiline
      rows={4}
      fullWidth
      label="Your Suggestion"
      value={suggestion}
      onChange={(e) => setSuggestion(e.target.value)}
      error={Boolean(submitError)}
      helperText={submitError}
      disabled={submitting}
      sx={{ mb: 2 }}
    />
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
        onChange={handleRecaptchaChange}
        onError={handleRecaptchaError}
      />
    </Box>
  </DialogContent>
  <DialogActions>
    <Button onClick={handleSuggestionClose} disabled={submitting}>
      Cancel
    </Button>
    <Button 
      onClick={handleSuggestionSubmit}
      variant="contained"
      disabled={!suggestion.trim() || !recaptchaToken || submitting}
    >
      Submit
    </Button>
  </DialogActions>
</Dialog>
    </StyledCard>
  );
};

export default DealCard;